import { useState } from "react";

import { Flex, Box, Checkbox, CheckboxGroup, useDisclosure } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useShallow } from "zustand/react/shallow";

import { RestockingPriorityTier } from "__graphql__/types";
import { EppoFeatureFlags } from "app/types/flags";
import { useEppoFeatureFlagProvider } from "shared/hooks/useEppoFeatureFlag";
import { BottomSheet } from "ui/BottomSheet/BottomSheet";
import { Button } from "ui/Button/Button";
import {
  AppleSliceIcon,
  CaretRightIcon,
  CaretLeftIcon,
  NumericListIcon,
  WarningThreeIcon,
} from "ui/Icons/Icons";
import { SlideFade } from "ui/Transitions/Transitions";
import { DetailL, TitleM } from "ui/Typography/Typography";

import { useRestockingServiceStore } from "../stores/restockingServiceStore";
import { RemoveFiltersModal } from "./modals/RemoveFiltersModal";

type VisibleFilter = "product-category" | "shelf-location" | "priority-tier" | null;

function CategoryFilters({
  selectedCategories,
  setSelectedCategories,
  setVisibleFilters,
}: {
  selectedCategories: string[];
  setSelectedCategories: (values: string[]) => void;
  setVisibleFilters: (filter: VisibleFilter) => void;
}) {
  const intl = useIntl();

  return (
    <Box w="100vw" maxW="22.5rem" px="s200" py="s100">
      <Box p="s100" position="relative" mb="s100">
        <TitleM textAlign="center" mb="s200">
          {intl.formatMessage({ id: "flows.inventory.restocking-list.filters.product-category" })}
        </TitleM>
        <CaretLeftIcon
          position="absolute"
          top="s200"
          left="s200"
          boxSize="24px"
          marginLeft="auto"
          onClick={() => setVisibleFilters(null)}
        />
      </Box>
      <Box px="s200" py="s300" backgroundColor="white" borderRadius="sm">
        <CheckboxGroup
          colorScheme="pinkFlink"
          value={selectedCategories}
          onChange={(values) => setSelectedCategories(values as string[])}
        >
          <Flex flexDirection="column" gap="s300">
            <Checkbox value="beer">
              {intl.formatMessage({
                id: "pages.tgtg-bag-selection-page.bag-guidelines.categories.beer",
              })}
            </Checkbox>
            <Checkbox value="dry">
              {intl.formatMessage({
                id: "pages.tgtg-bag-selection-page.bag-guidelines.categories.dry",
              })}
            </Checkbox>
            <Checkbox value="fresh">
              {intl.formatMessage({
                id: "pages.tgtg-bag-selection-page.bag-guidelines.categories.fresh",
              })}
            </Checkbox>
            <Checkbox value="frozen">
              {intl.formatMessage({
                id: "pages.tgtg-bag-selection-page.bag-guidelines.categories.frozen",
              })}
            </Checkbox>
            <Checkbox value="f_and_v">
              {intl.formatMessage({
                id: "pages.tgtg-bag-selection-page.bag-guidelines.categories.f_and_v",
              })}
            </Checkbox>
            <Checkbox value="soft_drinks">
              {intl.formatMessage({
                id: "pages.tgtg-bag-selection-page.bag-guidelines.categories.soft_drinks",
              })}
            </Checkbox>
            <Checkbox value="tobacco">
              {intl.formatMessage({
                id: "pages.tgtg-bag-selection-page.bag-guidelines.categories.tobacco",
              })}
            </Checkbox>
            <Checkbox value="wine_and_spirits">
              {intl.formatMessage({
                id: "pages.tgtg-bag-selection-page.bag-guidelines.categories.wine_and_spirits",
              })}
            </Checkbox>
          </Flex>
        </CheckboxGroup>
      </Box>
    </Box>
  );
}

function ShelfLocationFilters({
  selectedShelves,
  setSelectedShelves,
  setVisibleFilters,
}: {
  selectedShelves: string[];
  setSelectedShelves: (values: string[]) => void;
  setVisibleFilters: (filter: VisibleFilter) => void;
}) {
  const intl = useIntl();

  return (
    <Box w="100vw" maxW="22.5rem" px="s200" py="s100">
      <Box p="s100" position="relative" mb="s100">
        <TitleM textAlign="center">
          {intl.formatMessage({ id: "flows.inventory.restocking-list.filters.shelf-location" })}
        </TitleM>
        <CaretLeftIcon
          position="absolute"
          top="s100"
          left="0"
          boxSize="24px"
          marginLeft="auto"
          onClick={() => setVisibleFilters(null)}
        />
      </Box>
      <Box px="s200" py="s300" backgroundColor="white" borderRadius="sm">
        <CheckboxGroup
          colorScheme="pinkFlink"
          value={selectedShelves}
          onChange={(values) => setSelectedShelves(values as string[])}
        >
          <Flex flexDirection="column" gap="s300">
            <Checkbox value="001-028">001 - 028</Checkbox>
            <Checkbox value="029-056">029 - 056</Checkbox>
            <Checkbox value="057-084">057 - 084</Checkbox>
            <Checkbox value="085-112">085 - 112</Checkbox>
            <Checkbox value=">112">{"> 112"}</Checkbox>
          </Flex>
        </CheckboxGroup>
      </Box>
    </Box>
  );
}

function PriorityTierFilters({
  selectedPriorities,
  setSelectedPriorities,
  setVisibleFilters,
}: {
  selectedPriorities: string[];
  setSelectedPriorities: (values: string[]) => void;
  setVisibleFilters: (filter: VisibleFilter) => void;
}) {
  const intl = useIntl();

  return (
    <Box w="100vw" maxW="22.5rem" px="s200" py="s100">
      <Box p="s100" position="relative" mb="s100">
        <TitleM textAlign="center">
          {intl.formatMessage({ id: "flows.inventory.restocking-list.filters.priority-tier" })}
        </TitleM>
        <CaretLeftIcon
          position="absolute"
          top="s100"
          left="0"
          boxSize="24px"
          marginLeft="auto"
          onClick={() => setVisibleFilters(null)}
        />
      </Box>
      <Box px="s200" py="s300" backgroundColor="white" borderRadius="sm">
        <CheckboxGroup
          colorScheme="pinkFlink"
          value={selectedPriorities}
          onChange={(values) => setSelectedPriorities(values as string[])}
        >
          <Flex flexDirection="column" gap="s300">
            <Checkbox value={RestockingPriorityTier.high}>
              {intl.formatMessage({ id: "flows.inventory.restocking-list.high-priority-label" })}
            </Checkbox>
            <Checkbox value={RestockingPriorityTier.medium}>
              {intl.formatMessage({ id: "flows.inventory.restocking-list.med-priority-label" })}
            </Checkbox>
            <Checkbox value={RestockingPriorityTier.low}>
              {intl.formatMessage({ id: "flows.inventory.restocking-list.low-priority-label" })}
            </Checkbox>
          </Flex>
        </CheckboxGroup>
      </Box>
    </Box>
  );
}

function FiltersList({
  categoryFilters,
  shelfFilters,
  priorityFilters,
  setVisibleFilters,
  onApply,
  onRemoveAll,
}: {
  categoryFilters: string[];
  shelfFilters: string[];
  priorityFilters: string[];
  setVisibleFilters: (filter: VisibleFilter) => void;
  onApply: () => void;
  onRemoveAll: () => void;
}) {
  const intl = useIntl();
  const { isFeatureEnabled: isRestockingV2Enabled } = useEppoFeatureFlagProvider(
    EppoFeatureFlags.ENABLE_RESTOCKING_V2,
  );
  const hasActiveFilters =
    categoryFilters.length > 0 || shelfFilters.length > 0 || priorityFilters.length > 0;

  return (
    <Box p="s200" w="100vw" maxW="22.5rem">
      <TitleM textAlign="center" mb="s200">
        {intl.formatMessage({ id: "flows.inventory.restocking-list.filters.title" })}
      </TitleM>
      <Flex gap="s100" flexDirection="column" mb="s200">
        {isRestockingV2Enabled && (
          <Flex
            p="s200"
            gap="s200"
            backgroundColor="white"
            borderRadius="sm"
            alignItems="center"
            onClick={() => setVisibleFilters("priority-tier")}
          >
            <WarningThreeIcon boxSize="24px" />
            <Box>
              <TitleM>
                {intl.formatMessage({
                  id: "flows.inventory.restocking-list.filters.priority-tier",
                })}
              </TitleM>
              <DetailL>
                {intl.formatMessage(
                  { id: "flows.inventory.restocking-list.filters.n-selected" },
                  { amount: priorityFilters.length },
                )}
              </DetailL>
            </Box>
            <CaretRightIcon boxSize="24px" marginLeft="auto" />
          </Flex>
        )}
        <Flex
          p="s200"
          gap="s200"
          backgroundColor="white"
          borderRadius="sm"
          alignItems="center"
          onClick={() => setVisibleFilters("product-category")}
        >
          <AppleSliceIcon boxSize="24px" />
          <Box>
            <TitleM>
              {intl.formatMessage({
                id: "flows.inventory.restocking-list.filters.product-category",
              })}
            </TitleM>
            <DetailL>
              {intl.formatMessage(
                { id: "flows.inventory.restocking-list.filters.n-selected" },
                { amount: categoryFilters.length },
              )}
            </DetailL>
          </Box>
          <CaretRightIcon boxSize="24px" marginLeft="auto" />
        </Flex>
        <Flex
          p="s200"
          gap="s200"
          backgroundColor="white"
          borderRadius="sm"
          alignItems="center"
          onClick={() => setVisibleFilters("shelf-location")}
        >
          <NumericListIcon boxSize="24px" />
          <Box>
            <TitleM>
              {intl.formatMessage({
                id: "flows.inventory.restocking-list.filters.shelf-location",
              })}
            </TitleM>
            <DetailL>
              {intl.formatMessage(
                { id: "flows.inventory.restocking-list.filters.n-selected" },
                { amount: shelfFilters.length },
              )}
            </DetailL>
          </Box>
          <CaretRightIcon boxSize="24px" marginLeft="auto" />
        </Flex>
      </Flex>
      {hasActiveFilters && (
        <Flex gap="s100">
          <Button variant="primary" bgColor="red.500" flex="1" onClick={onRemoveAll}>
            {intl.formatMessage({ id: "flows.inventory.restocking-list.filters.remove-all" })}
          </Button>
          <Button variant="primary" flex="1" onClick={onApply}>
            {intl.formatMessage({ id: "flows.inventory.restocking-list.filters.apply-filter" })}
          </Button>
        </Flex>
      )}
    </Box>
  );
}

export function RestockingFiltersBottomSheet({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    categoryFilters,
    shelfFilters,
    priorityFilters,
    updateCategoryFilters,
    updateShelfFilters,
    updatePriorityFilters,
  } = useRestockingServiceStore(
    useShallow((state) => ({
      categoryFilters: state.categoryFilters,
      shelfFilters: state.shelfFilters,
      priorityFilters: state.priorityFilters,
      updateCategoryFilters: state.updateCategoryFilters,
      updateShelfFilters: state.updateShelfFilters,
      updatePriorityFilters: state.updatePriorityFilters,
    })),
  );

  const [visibleFilters, setVisibleFilters] = useState<VisibleFilter>(null);
  const {
    isOpen: isRemoveModalOpen,
    onOpen: onOpenRemoveModal,
    onClose: onCloseRemoveModal,
  } = useDisclosure();

  const handleApply = () => {
    setVisibleFilters(null);
    onClose();
  };

  const handleConfirmRemoveAll = () => {
    updateCategoryFilters([]);
    updateShelfFilters([]);
    updatePriorityFilters([]);
    onCloseRemoveModal();
    setVisibleFilters(null);
    onClose();
  };

  const handleClose = () => {
    setVisibleFilters(null);
    onClose();
  };

  return (
    <>
      <BottomSheet isOpen={isOpen} onClose={handleClose} backgroundColor="grey.150">
        <Flex overflow="hidden" w="full" alignItems="flex-end">
          <SlideFade in={visibleFilters === null} offsetY={"50px"} unmountOnExit>
            <FiltersList
              categoryFilters={categoryFilters}
              shelfFilters={shelfFilters}
              priorityFilters={priorityFilters}
              setVisibleFilters={setVisibleFilters}
              onApply={handleApply}
              onRemoveAll={onOpenRemoveModal}
            />
          </SlideFade>
          <SlideFade in={visibleFilters === "product-category"} offsetX="100px" unmountOnExit>
            <CategoryFilters
              selectedCategories={categoryFilters}
              setSelectedCategories={updateCategoryFilters}
              setVisibleFilters={setVisibleFilters}
            />
          </SlideFade>
          <SlideFade in={visibleFilters === "shelf-location"} offsetX="100px" unmountOnExit>
            <ShelfLocationFilters
              selectedShelves={shelfFilters}
              setSelectedShelves={updateShelfFilters}
              setVisibleFilters={setVisibleFilters}
            />
          </SlideFade>
          <SlideFade in={visibleFilters === "priority-tier"} offsetX="100px" unmountOnExit>
            <PriorityTierFilters
              selectedPriorities={priorityFilters}
              setSelectedPriorities={updatePriorityFilters}
              setVisibleFilters={setVisibleFilters}
            />
          </SlideFade>
        </Flex>
      </BottomSheet>
      <RemoveFiltersModal
        isOpen={isRemoveModalOpen}
        onClose={onCloseRemoveModal}
        onConfirm={handleConfirmRemoveAll}
      />
    </>
  );
}
